import type { UseQueryOptions } from "react-query";
import { useQuery } from "react-query";
import { z } from "zod";

import { useApiClient } from "api/useApiClient";

const optString = z.string().optional().nullable();

const AddressAutoCompleteArgsSchema = z.object({
  query: z.string().min(1, "Query string must be at least 1 character long"),
  countryCode: z.string().optional(),
});
export type AddressAutoCompleteArgs = z.infer<typeof AddressAutoCompleteArgsSchema>;

const AddressSchema = z.object({
  title: optString,
  id: optString,
  language: optString,
  resultType: optString,
  localityType: optString,
  houseNumberType: optString,
  address: z.object({
    label: optString,
    countryCode: optString,
    countryName: optString,
    stateCode: optString,
    state: optString,
    county: optString,
    city: optString,
    street: optString,
    postalCode: optString,
    houseNumber: optString,
  }),
});
export type Address = z.infer<typeof AddressSchema>;

export const AddressResponseSchema = z.object({
  items: z.array(AddressSchema),
});
type AddressResponse = z.infer<typeof AddressResponseSchema>;

export const queryKeyPrefix = "addressAutocomplete";
type QueryKey = [typeof queryKeyPrefix, string, string?];
export const getAddressAutocompleteKey = (args: AddressAutoCompleteArgs): QueryKey => [
  queryKeyPrefix,
  args.query,
  args.countryCode,
];

export const useAddressAutocomplete = (
  args: AddressAutoCompleteArgs,
  options?: UseQueryOptions<AddressResponse, Error, AddressResponse, QueryKey>
) => {
  const client = useApiClient();

  return useQuery(getAddressAutocompleteKey(args), () => client.addressAutoComplete(args), options);
};
